import React, { useState, useEffect } from 'react';
import { AlertTriangle, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import Modal from '../shared/Modal';
import {
  CANCEL_ENDPOINTS, parseCancelQuote, confidenceNote, unwrapBooking, isCancelledStatus,
} from '../../utils/flightBooking';

const NUITEE_KEY = process.env.REACT_APP_NUITEE_API_KEY || '';

const money = (p) => p ? `${p.currency === 'AUD' ? 'A$' : (p.currency || '') + ' '}${(parseFloat(p.amount) || 0).toFixed(2)}` : '—';

const api = async (url, method = 'GET') => {
  const res = await fetch(url, {
    method,
    headers: { 'X-API-Key': NUITEE_KEY, 'Content-Type': 'application/json', accept: 'application/json' },
    ...(method !== 'GET' ? { body: '{}' } : {}),
  });
  const text = await res.text();
  let json = null;
  try { json = text ? JSON.parse(text) : null; } catch { /* cancel returns an empty body */ }
  return { ok: res.ok, status: res.status, json };
};

/**
 * Cancel a ticketed flight.
 *
 * ⚠️ Built around the fact that the cancel endpoint cannot be trusted to report the truth. Measured
 * in sandbox: `PUT .../cancel` returns 200 with an empty body and leaves the booking untouched, and
 * a second call returns 200 as well. So this always re-reads the booking afterwards and only says
 * "cancelled" when the status actually says so. Anything else is reported as unconfirmed, with the
 * reference needed to chase it — telling someone a client's flight is cancelled when it is not is
 * far worse than telling them to check.
 */
export default function FlightCancelModal({ bookingId, bookingRef, flightLabel, onClose, onCancelled }) {
  const [quote, setQuote]       = useState(null);
  const [quoteErr, setQuoteErr] = useState('');
  const [loading, setLoading]   = useState(true);
  const [busy, setBusy]         = useState(false);
  const [outcome, setOutcome]   = useState(null);   // { state: 'cancelled'|'unconfirmed'|'failed', detail }
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await api(CANCEL_ENDPOINTS.quote(bookingId));
      if (cancelled) return;
      if (r.ok) setQuote(parseCancelQuote(r.json));
      else setQuoteErr(r.json?.error?.description || `The airline would not quote this cancellation (${r.status}).`);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [bookingId]);

  const doCancel = async () => {
    setBusy(true);
    setOutcome(null);
    try {
      const r = await api(CANCEL_ENDPOINTS.cancel(bookingId), 'PUT');
      if (!r.ok) {
        setOutcome({ state: 'failed', detail: r.json?.error?.description || `The airline rejected the cancellation (${r.status}).` });
        setBusy(false);
        return;
      }
      // The 200 means nothing on its own. Read the booking back and believe only that.
      await new Promise(res => setTimeout(res, 2500));
      const check = await api(CANCEL_ENDPOINTS.get(bookingId));
      const bk = check.ok ? unwrapBooking(check.json) : null;
      const status = bk?.status || '';

      if (isCancelledStatus(status)) {
        setOutcome({ state: 'cancelled', detail: status });
        onCancelled?.({ status, refund: quote?.refund || null, penalty: quote?.penalty || null });
      } else {
        setOutcome({
          state: 'unconfirmed',
          detail: status
            ? `The airline still reports this booking as ${status}.`
            : 'The booking could not be re-read to confirm.',
        });
      }
    } catch (e) {
      setOutcome({ state: 'failed', detail: e.message || 'The cancellation request failed.' });
    } finally {
      setBusy(false);
    }
  };

  const armed = confirmText.trim().toUpperCase() === 'CANCEL';

  return (
    <Modal title="Cancel this flight" onClose={onClose}>
      <div className="space-y-4">
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm text-gray-900">{flightLabel}</p>
          <p className="text-xs text-gray-700 mt-0.5">Booking reference <strong>{bookingRef || bookingId}</strong></p>
        </div>

        {loading && (
          <p className="text-sm text-gray-700 flex items-center gap-2">
            <RefreshCw size={14} aria-hidden="true" className="animate-spin" /> Asking the airline what a cancellation would cost…
          </p>
        )}

        {!loading && quote && (
          <div className="border border-gray-200 rounded-lg p-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-700">Refund</span>
              <span className="font-semibold text-gray-900 tabular-nums">{money(quote.refund)}</span>
            </div>
            {quote.penalty && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">Penalty</span>
                <span className="text-gray-900 tabular-nums">{money(quote.penalty)}</span>
              </div>
            )}
            {quote.penalties.length > 0 && (
              <ul className="text-xs text-gray-700 space-y-0.5 pt-1 border-t border-gray-100">
                {quote.penalties.map((p, i) => (
                  <li key={i} className="flex justify-between gap-3">
                    <span>{p.description || p.type}</span>
                    <span className="tabular-nums">{p.amount != null ? money({ amount: p.amount, currency: quote.refund?.currency }) : '—'}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-gray-700 pt-1 border-t border-gray-100">
              {quote.isVoidable ? 'Within the free-cancellation window. ' : ''}
              {quote.isRefundable === false ? 'This fare is non-refundable. ' : ''}
              {/* The API grades its own answer; passing that on matters, because an "estimated"
                  figure repeated to a client as final becomes a promise nobody made. */}
              <strong>{quote.confidence}:</strong> {confidenceNote(quote.confidence)}
            </p>
          </div>
        )}

        {!loading && quoteErr && (
          <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg">
            <p className="text-sm text-amber-900">
              <strong>No refund quote available.</strong> {quoteErr}
            </p>
            <p className="text-xs text-amber-900 mt-1">
              You can still request the cancellation, but nobody can tell you what will be refunded
              until the airline settles it. Consider calling them instead.
            </p>
          </div>
        )}

        {!outcome && (
          <>
            <div className="p-3 bg-red-50 border-2 border-red-300 rounded-lg">
              <p className="text-sm text-red-900 flex items-start gap-2">
                <AlertTriangle size={15} aria-hidden="true" className="mt-0.5 shrink-0 text-red-700" />
                <span>
                  Cancelling cannot be undone, and the fare will not be held. Type <strong>CANCEL</strong> to confirm.
                </span>
              </p>
              <input
                className="mt-2 w-full border border-red-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 placeholder:text-gray-500"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder="CANCEL"
                aria-label="Type CANCEL to confirm"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                Keep the booking
              </button>
              <button
                onClick={doCancel}
                disabled={!armed || busy}
                className="px-5 py-2 bg-red-700 text-white text-sm rounded-lg hover:bg-red-800 disabled:opacity-50"
              >
                {busy ? 'Cancelling…' : 'Cancel this flight'}
              </button>
            </div>
          </>
        )}

        {outcome?.state === 'cancelled' && (
          <div className="p-3 bg-green-50 border border-green-300 rounded-lg">
            <p className="text-sm text-green-900 flex items-start gap-2">
              <CheckCircle size={15} aria-hidden="true" className="mt-0.5 shrink-0 text-green-700" />
              <span>
                <strong>Cancelled.</strong> The airline now reports this booking as {outcome.detail}.
                {quote?.refund && <> A refund of {money(quote.refund)} was quoted — confirm it lands.</>}
              </span>
            </p>
          </div>
        )}

        {outcome?.state === 'unconfirmed' && (
          <div className="p-3 bg-amber-50 border-2 border-amber-400 rounded-lg">
            <p className="text-sm text-amber-900 flex items-start gap-2">
              <AlertTriangle size={15} aria-hidden="true" className="mt-0.5 shrink-0 text-amber-700" />
              <span>
                <strong>Not confirmed.</strong> The request was accepted but the booking has not
                changed state. {outcome.detail} <strong>Do not assume it is cancelled</strong> — call
                the airline with reference {bookingRef || bookingId} and confirm before telling the
                traveller anything.
              </span>
            </p>
          </div>
        )}

        {outcome?.state === 'failed' && (
          <div className="p-3 bg-red-50 border border-red-300 rounded-lg">
            <p className="text-sm text-red-900 flex items-start gap-2">
              <XCircle size={15} aria-hidden="true" className="mt-0.5 shrink-0 text-red-700" />
              <span><strong>Not cancelled.</strong> {outcome.detail} The booking still stands.</span>
            </p>
          </div>
        )}

        {outcome && (
          <div className="flex justify-end">
            <button onClick={onClose} className="px-5 py-2 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-900">
              Close
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
